"use client";

import React, { useCallback, useEffect, useRef, useState } from "react";
import ReactDOM from "react-dom";
import { createClasses } from "@bloomberg/media-ui-core";
import styles from "./styles.module.css";

const ANIMATION_DURATION = 400;
const LIGHTBOX_PADDING = 24;

type AnimatingState = "idle" | "in" | "out";

type Rect = { top: number; left: number; width: number; height: number };

function computeExpandedRect(): Rect {
  const width = Math.round(globalThis.innerWidth * 0.7);
  const height = Math.round((width * 9) / 16);

  return {
    top: (globalThis.innerHeight - height) / 2 - LIGHTBOX_PADDING,
    left: (globalThis.innerWidth - width) / 2 - LIGHTBOX_PADDING,
    width: width + LIGHTBOX_PADDING * 2,
    height: height + LIGHTBOX_PADDING * 2,
  };
}

export type LightboxRenderProps = {
  readonly isExpanded: boolean;
  readonly expandedDimensions: {
    readonly width: number;
    readonly height: number;
  } | null;
  readonly onToggleExpand: () => void;
  readonly expandButtonRef: React.RefObject<HTMLButtonElement>;
};

type LightboxContainerProps = {
  readonly className?: string;
  readonly children: (props: LightboxRenderProps) => React.ReactNode;
};

function LightboxContainerInner({
  className,
  children,
}: LightboxContainerProps) {
  const [isExpanded, setIsExpanded] = useState(false);
  const [showPortal, setShowPortal] = useState(false);
  const [animating, setAnimating] = useState<AnimatingState>("idle");

  const expandButtonRef = useRef<HTMLButtonElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const lightboxRef = useRef<HTMLDivElement>(null);
  const originRectRef = useRef<Rect | null>(null);

  const [expandedDimensions, setExpandedDimensions] = useState<{
    width: number;
    height: number;
  } | null>(null);

  useEffect(() => {
    if (!isExpanded) {
      setExpandedDimensions(null);

      return undefined;
    }

    const compute = () => {
      const w = Math.round(globalThis.innerWidth * 0.7);

      setExpandedDimensions({
        width: w,
        height: Math.round((w * 9) / 16) - 1.5 * LIGHTBOX_PADDING,
      });
    };

    compute();
    globalThis.addEventListener("resize", compute);

    return () => globalThis.removeEventListener("resize", compute);
  }, [isExpanded]);

  const handleExpand = useCallback(() => {
    const container = containerRef.current;

    if (!container) return;

    const rect = container.getBoundingClientRect();

    originRectRef.current = {
      top: rect.top,
      left: rect.left,
      width: rect.width,
      height: rect.height,
    };

    setIsExpanded(true);
    setShowPortal(true);
    setAnimating("in");
  }, []);

  const handleCollapse = useCallback(() => {
    if (!originRectRef.current) {
      setIsExpanded(false);
      setShowPortal(false);

      return;
    }

    setAnimating("out");
    setIsExpanded(false);

    setTimeout(() => {
      setShowPortal(false);
      setAnimating("idle");
      requestAnimationFrame(() => {
        expandButtonRef.current?.focus();
      });
    }, ANIMATION_DURATION);
  }, []);

  useEffect(() => {
    const lightbox = lightboxRef.current;
    const origin = originRectRef.current;

    if (!lightbox || !origin) return;

    if (animating === "in") {
      lightbox.style.transition = "none";
      lightbox.style.top = `${origin.top}px`;
      lightbox.style.left = `${origin.left}px`;
      lightbox.style.width = `${origin.width}px`;
      lightbox.style.height = `${origin.height}px`;
      lightbox.style.borderRadius = "0px";

      lightbox.getBoundingClientRect();
      lightbox.style.transition = "";

      const expanded = computeExpandedRect();

      lightbox.style.top = `${expanded.top}px`;
      lightbox.style.left = `${expanded.left}px`;
      lightbox.style.width = `${expanded.width}px`;
      lightbox.style.height = `${expanded.height}px`;
      lightbox.style.borderRadius = "";
    }

    if (animating === "out") {
      lightbox.style.top = `${origin.top}px`;
      lightbox.style.left = `${origin.left}px`;
      lightbox.style.width = `${origin.width}px`;
      lightbox.style.height = `${origin.height}px`;
      lightbox.style.borderRadius = "0px";
    }
  }, [animating]);

  useEffect(() => {
    if (!isExpanded || animating !== "in") return undefined;

    const onResize = () => {
      const lightbox = lightboxRef.current;

      if (!lightbox) return;

      lightbox.style.transition = "none";
      const expanded = computeExpandedRect();

      lightbox.style.top = `${expanded.top}px`;
      lightbox.style.left = `${expanded.left}px`;
      lightbox.style.width = `${expanded.width}px`;
      lightbox.style.height = `${expanded.height}px`;

      lightbox.getBoundingClientRect();
      lightbox.style.transition = "";
    };

    globalThis.addEventListener("resize", onResize);

    return () => globalThis.removeEventListener("resize", onResize);
  }, [isExpanded, animating]);

  const handleToggleExpand = useCallback(() => {
    if (isExpanded) {
      handleCollapse();
    } else {
      handleExpand();
    }
  }, [isExpanded, handleCollapse, handleExpand]);

  const handleBackdropClick = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      if (e.target === e.currentTarget) {
        handleCollapse();
      }
    },
    [handleCollapse]
  );

  useEffect(() => {
    if (!isExpanded) return undefined;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        handleCollapse();
      }
    };

    document.addEventListener("keydown", handleKeyDown);

    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [isExpanded, handleCollapse]);

  const content = children({
    isExpanded,
    expandedDimensions,
    onToggleExpand: handleToggleExpand,
    expandButtonRef,
  });

  if (showPortal) {
    return (
      <>
        <div
          ref={containerRef}
          className={createClasses([
            styles.container,
            styles.placeholder,
            className,
          ])}
        />
        {ReactDOM.createPortal(
          <div
            className={styles.lightboxBackdrop}
            data-animating={animating === "idle" ? "in" : animating}
            onClick={handleBackdropClick}
            role="dialog"
            aria-modal="true"
            aria-label="Chart expanded view"
          >
            <div ref={lightboxRef} className={styles.lightboxInner}>
              {content}
            </div>
          </div>,
          document.body
        )}
      </>
    );
  }

  return (
    <div ref={containerRef} className={className}>
      {content}
    </div>
  );
}

LightboxContainerInner.displayName = "LightboxContainer";

const LightboxContainer = React.memo(LightboxContainerInner);

export default LightboxContainer;
